import React from 'react';
import { AlertTriangle, X, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const EscalationModal = ({ 
  isOpen, 
  onClose, 
  triageResult,
  selectedPackage,
  onContinueWithPackage,
  onUpgradeToFullCase 
}) => {
  if (!isOpen || !triageResult) return null;

  const { score, reason, suggested_action } = triageResult;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full shadow-xl border border-gray-200 animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                Din sak kan være mer kompleks
              </h2>
              <p className="text-sm text-gray-600">
                Basert på vår AI-analyse virker saken din mer omfattende enn {selectedPackage?.name}-pakken.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Analysis */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-900 mb-2">
              Hvorfor mener AI dette?
            </h3>
            <p className="text-sm text-amber-800">
              {reason}
            </p>
            {suggested_action && (
              <p className="text-sm text-amber-700 mt-2 font-medium">
                💡 {suggested_action}
              </p>
            )}
          </div>

          {/* Complexity Score Indicator */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Kompleksitetsscore</span>
              <span className="text-lg font-bold text-gray-900">{score}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-amber-500 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(score, 100)}%` }}
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Velg hvordan du vil fortsette:
            </h3>

            {/* Option 1: Continue with package */}
            <button
              onClick={onContinueWithPackage}
              className="w-full p-4 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-left transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 group-hover:bg-gray-200 rounded flex items-center justify-center flex-shrink-0 transition-colors">
                  <CheckCircle className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Fortsett med {selectedPackage?.name}-pakken
                  </h4>
                  <p className="text-sm text-gray-600">
                    Advokaten vil gjøre sitt beste innenfor pakke-rammene. 
                    Fastpris: {selectedPackage?.price?.toLocaleString('nb-NO')} kr
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    ℹ️ Leveransen vil være begrenset til det pakken inkluderer
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
            </button>

            {/* Option 2: Upgrade to full case */}
            <button
              onClick={onUpgradeToFullCase}
              className="w-full p-4 border-2 border-gray-900 bg-gray-900 hover:bg-gray-800 rounded-lg text-left transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-white/20 group-hover:bg-white/30 rounded flex items-center justify-center flex-shrink-0 transition-colors">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-1">
                    Få tilpasset tilbud fra advokat
                  </h4>
                  <p className="text-sm text-gray-200">
                    Advokaten vurderer saken og gir deg et skreddersydd tilbud 
                    basert på faktisk omfang og kompleksitet.
                  </p>
                  <p className="text-xs text-gray-300 mt-2">
                    ⭐ Anbefalt for komplekse saker
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <p className="text-xs text-gray-600 text-center">
            💡 Dette er en automatisk vurdering. Du kan alltid kontakte oss for veiledning.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EscalationModal;
